import { Checkbox, Text, Anchor, Drawer, Stack, Title, ScrollArea, Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import styles from './PrivacyConsent.module.css';

// Default texts
const DEFAULT_CONSENT_TEXT = `I declare that I have read and understood the Privacy Policy and consent to the processing of my personal data for application purposes.`

// const DEFAULT_TERMS_TEXT = `Privacy Notice – Personal Data Processing`;

/**
 * Privacy consent component with checkbox, short text with link,
 * and drawer/external link for full terms
 */
export const PrivacyConsent = ({
  checked,
  onChange,
  error,
  siteConfig = {},
  renderAs = 'box'
}) => {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  // Get texts from siteConfig or use defaults
  const consentText = siteConfig.privacyConsentText || DEFAULT_CONSENT_TEXT;
  const agencyLegalName = siteConfig.agencyLegalName || 'Agency Legal Name';
  const agencyAddress = siteConfig.agencyAddress || 'Address';
  const agencyEmail = siteConfig.agencyEmail || 'privacy@email.com';
  const termsText = siteConfig.privacyTermsText || '';
  const termsLink = siteConfig.privacyTermsLink;

  const DEFAULT_DISCLAIMER_TEXT = `This application form is managed by ${agencyLegalName}, with registered office in ${agencyAddress}, email ${agencyEmail}.\nThe data are collected through the Netwalk Application Platform, operated by GAP Multimedia S.r.l., which acts as Data Processor on behalf of the Agency, in accordance with Article 28 of the GDPR. The personal data provided will be processed for the purpose of evaluating the application and possible future professional collaboration. For complete information about the processing of personal data, please read the Privacy Policy of ${agencyLegalName}`;

  const disclaimerText = siteConfig.disclaimerText || DEFAULT_DISCLAIMER_TEXT;

  // 'plain' drops the padded box: no background and no border, just the text.
  const consentBoxClassName =
    renderAs === 'plain'
      ? undefined
      : [styles.privacyConsent, error && styles.privacyConsentError].filter(Boolean).join(' ');

  // Open the local drawer when no external privacy URL is configured.
  const handleLinkClick = (e) => {
    e.preventDefault();
    openDrawer();
  };

  const renderPrivacyAnchor = (label) => {
    if (termsLink) {
      return (
        <Anchor
          href={termsLink}
          target="_blank"
          rel="noopener noreferrer"
          inherit
          className={styles.privacyConsentLink}
        >
          {label}
        </Anchor>
      );
    }

    return (
      <Anchor
        href="#privacy-terms"
        onClick={handleLinkClick}
        inherit
        className={styles.privacyConsentLink}
      >
        {label}
      </Anchor>
    );
  };

  // Helper to render text with "Privacy Notice" / "Privacy Policy" as clickable links
  const renderTextWithPrivacyLink = (text, fallbackLinkLabel, size = "sm") => {
    const linkTexts = ['Privacy Notice', 'Privacy Policy'];
    for (const linkText of linkTexts) {
      const parts = text.split(linkText);
      if (parts.length === 2) {
        return (
          <Text component="span" size={size}>
            {parts[0]}
            {renderPrivacyAnchor(linkText)}
            {parts[1]}
          </Text>
        );
      }
    }

    if (fallbackLinkLabel) {
      return (
        <Text component="span" size={size}>
          {text}{' '}
          {renderPrivacyAnchor(fallbackLinkLabel)}
        </Text>
      );
    }

    return <Text component="span" size={size}>{text}</Text>;
  };

  const renderConsentText = () => renderTextWithPrivacyLink(consentText, 'View Privacy Notice');
  const renderDisclaimerText = () => renderTextWithPrivacyLink(disclaimerText, null, "xs");

  return (
    <>
      <div className={styles.privacyDisclaimer}>
        {renderDisclaimerText()}
      </div>
      <Box className={consentBoxClassName}>
        <Checkbox
          checked={checked}
          onChange={(event) => onChange(event.currentTarget.checked)}
          label={renderConsentText()}
          error={error}
          classNames={{
            root: styles.privacyConsentCheckbox,
            body: styles.privacyConsentCheckboxBody,
            inner: styles.privacyConsentCheckboxInner,
            input: styles.privacyConsentCheckboxInput,
            label: styles.privacyConsentLabel,
            error: styles.privacyConsentErrorText
          }}
        />
      </Box>

      {/* Drawer with full privacy terms */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={<Title order={3}>Privacy Notice</Title>}
        position="right"
        size="lg"
        padding="xl"
        classNames={{
          body: styles.privacyDrawerBody
        }}
      >
        <ScrollArea h="calc(100vh - 120px)" type="auto">
          <Stack gap="md">
            {termsText.split('\n\n').map((paragraph, index) => (
              <Text key={index} size="sm" className={styles.privacyTermsParagraph}>
                {paragraph}
              </Text>
            ))}
          </Stack>
        </ScrollArea>
      </Drawer>
    </>
  );
};

export default PrivacyConsent;
